import * as Sentry from '@sentry/react';
import {PaymentRequest, Stripe} from '@stripe/stripe-js';
import {loadStripeWithRetry} from '../../utils/stripeLoader';
import axios from 'axios';
import Analytics from '../../utils/Analytics';
import Environment from '../../utils/Environment';
import {BASE_URL} from '../../utils/dynamicCodes/hooks';
import {UserAlreadySubscribedMessage} from '../../utils/dynamicCodes/redirects';

import {stripeStyle} from './screens/EmailConfirmationCheckoutScreen';

import {SupportedCountryCode} from '../../utils/currency';
import {growthBook} from '../../index';
import {PaymentType, PaywallSlug} from '../../analytics/paywallEvents';
import {trackPaymentMethodShown} from '../../analytics/trackPaymentMethodShown';
import {runWalletCheck} from '../../utils/walletPrefetch';

type CreateClientSecretResult = {
  client_secret: string;
  person_id: string;
  charge_date: string;
  options: Record<string, any>;
};
type CreateClientSecretErrorResult = {
  duplicateCustomer: boolean;
  error: boolean;
};
export const createCheckoutDetails = async (
  token: string,
  winback = false,
): Promise<CreateClientSecretResult | CreateClientSecretErrorResult> => {
  try {
    const response = await axios.post(`${BASE_URL}/api/stripe-setup-checkout`, {
      token,
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      winback,
    });
    const {client_secret, person_id, charge_date} = response.data;

    if (person_id) {
      Analytics.track('Identified user via API', {person_id});
      Analytics.identify(person_id);

      // We need to set the GB personId before we retrieve the feature value
      if (!growthBook.getAttributes().personId) {
        growthBook.setAttributes({personId: person_id});
      }
    }

    return {
      client_secret,
      person_id,
      charge_date,
      options: {
        clientSecret: client_secret,
        appearance: {
          variables: {
            ...stripeStyle,
          },
        },
      },
    };
  } catch (error: any) {
    if (error.response?.data === UserAlreadySubscribedMessage) {
      return {duplicateCustomer: true, error: false};
    } else {
      Sentry.captureException(new Error(error));

      return {duplicateCustomer: false, error: true};
    }
  }
};

/** Resolves once Stripe.js has loaded, retrying and showing `StripeLoadFallback` when it can't (see stripeLoader). */
export const createStripeInstance = (): Promise<Stripe | null> => loadStripeWithRetry('emailConfirmation');

/**
 * Build the payment request and run the wallet check without reporting it. Returns the request
 * only when Apple or Google Pay is available, plus the methods the user will be offered, so a
 * caller that shows the button later than the check finishes can fire `trackPaymentMethodShown`
 * itself. Everyone else wants `createPaymentRequest`.
 */
export const checkPaymentRequest = async (
  stripeInstance: Stripe,
  amount: number, //What will appear in the sheet, in USD cents
  label: string,
  locale: SupportedCountryCode,
): Promise<{paymentRequest?: PaymentRequest; paymentTypes: PaymentType[]}> =>
  runWalletCheck(stripeInstance, amount, label, locale);

export const createPaymentRequest = async (
  stripeInstance: Stripe,
  code: string,
  amount: number, //What will appear in the sheet, in USD cents
  label: string,
  locale: SupportedCountryCode,
  paywall?: PaywallSlug,
): Promise<PaymentRequest | undefined> => {
  const {paymentRequest, paymentTypes} = await checkPaymentRequest(stripeInstance, amount, label, locale);
  paymentTypes.forEach(paymentType => trackPaymentMethodShown(paymentType, {code, paywall}));
  return paymentRequest;
};
